import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { errMsg, toCents } from "@/lib/api";

const Field = ({ label, hint, children }) => (
  <label className="block"><span className="text-sm font-semibold">{label}</span>{children}{hint && <span className="mt-1 block text-xs text-muted2">{hint}</span>}</label>
);

const TZS = [["America/St_Johns", "Newfoundland"], ["America/Halifax", "Atlantic"], ["America/Toronto", "Eastern"], ["America/Winnipeg", "Central"],
  ["America/Regina", "Saskatchewan"], ["America/Edmonton", "Mountain"], ["America/Vancouver", "Pacific"], ["America/Whitehorse", "Yukon"]];

export default function SettingsPanel({ api, venue, reload }) {
  const [f, setF] = useState({
    name: venue.name, legal_name: venue.legal_name || "", address: venue.address || "", hours: venue.hours || "", description: venue.description || "",
    logo: venue.logo, min_order: ((venue.min_order_cents || 0) / 100).toFixed(2), conv_fee: ((venue.convenience_fee_cents || 0) / 100).toFixed(2),
    prep_minutes: venue.prep_minutes, charge_gst: venue.charge_gst, gst_rate: venue.gst_rate, gst_number: venue.gst_number || "",
    reset_daily: !!venue.reset_daily, opening_time: venue.opening_time || "06:00", timezone: venue.timezone || "America/Edmonton",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 250000) return toast.error("Logo must be under 250 KB");
    const r = new FileReader();
    r.onload = () => setF((x) => ({ ...x, logo: r.result }));
    r.readAsDataURL(file);
  };
  const save = async () => {
    if (f.charge_gst && !f.gst_number.trim()) toast.warning("Add your GST/HST number — orders stay paused until you do.");
    try {
      await api.put(`/venues/${venue.id}`, { name: f.name, legal_name: f.legal_name, address: f.address, hours: f.hours, description: f.description, logo: f.logo || undefined,
        min_order_cents: toCents(f.min_order), convenience_fee_cents: toCents(f.conv_fee), prep_minutes: Number(f.prep_minutes) || 0,
        charge_gst: f.charge_gst, gst_rate: Number(f.gst_rate), gst_number: f.gst_number.trim(),
        reset_daily: f.reset_daily, opening_time: f.opening_time, timezone: f.timezone });
      toast.success("Settings saved"); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2" data-testid="settings-panel">
      <div className="card-lg grid gap-4 p-5">
        <h3 className="text-lg font-extrabold">Venue profile</h3>
        <Field label="Venue name"><Input value={f.name} onChange={set("name")} data-testid="settings-name-input" /></Field>
        <Field label="Legal business name" hint="Shown on receipts."><Input value={f.legal_name} onChange={set("legal_name")} data-testid="settings-legal-name-input" /></Field>
        <Field label="Address"><Input value={f.address} onChange={set("address")} data-testid="settings-address-input" /></Field>
        <Field label="Hours"><Input value={f.hours} onChange={set("hours")} data-testid="settings-hours-input" /></Field>
        <Field label="Short description"><Textarea value={f.description} onChange={set("description")} data-testid="settings-description-input" /></Field>
        <Field label="Logo"><div className="mt-1 flex items-center gap-3">{f.logo && <img src={f.logo} alt="" className="h-12 w-12 rounded-xl object-cover" />}
          <input type="file" accept="image/*" onChange={onLogo} data-testid="settings-logo-input" className="text-sm" /></div></Field>
      </div>
      <div className="card-lg grid content-start gap-4 p-5">
        <h3 className="text-lg font-extrabold">Ordering & tax</h3>
        <Field label="Minimum order (pre-tax, CAD)"><Input inputMode="decimal" value={f.min_order} onChange={set("min_order")} data-testid="settings-min-order-input" /></Field>
        <Field label="Customer convenience fee (CAD, 0 = none)" hint="Added to each order and paid to you. GST applies to it."><Input inputMode="decimal" value={f.conv_fee} onChange={set("conv_fee")} data-testid="settings-conv-fee-input" /></Field>
        <Field label="Estimated prep time (minutes)"><Input inputMode="numeric" value={f.prep_minutes} onChange={set("prep_minutes")} data-testid="settings-prep-input" /></Field>
        <div className="flex items-center justify-between rounded-2xl bg-brand-pale p-4">
          <div><div className="font-semibold">Charge GST</div><div className="text-xs text-muted2">Default 5% (Alberta). Use your HST or GST+PST rate elsewhere.</div></div>
          <Switch checked={f.charge_gst} onCheckedChange={set("charge_gst")} data-testid="settings-charge-gst-switch" />
        </div>
        {f.charge_gst && <>
          <Field label="GST/HST rate (%)"><Input inputMode="decimal" value={f.gst_rate} onChange={set("gst_rate")} data-testid="settings-gst-rate-input" /></Field>
          <Field label="GST/HST registration number" hint="Required before you can accept orders while GST is on."><Input value={f.gst_number} onChange={set("gst_number")} placeholder="123456789 RT0001" data-testid="settings-gst-number-input" /></Field>
        </>}
        <div className="rounded-2xl bg-brand-pale p-4" data-testid="order-numbering-card">
          <div className="flex items-center justify-between">
            <div><div className="font-semibold">Reset order numbers daily</div><div className="text-xs text-muted2">Off: numbers keep counting up (#1, #2, #3…). On: back to #1 every day at opening time.</div></div>
            <Switch checked={f.reset_daily} onCheckedChange={set("reset_daily")} data-testid="settings-reset-daily-switch" />
          </div>
          {f.reset_daily && <div className="mt-3 grid grid-cols-2 gap-3">
            <Field label="Opening time"><Input type="time" value={f.opening_time} onChange={set("opening_time")} data-testid="settings-opening-time-input" /></Field>
            <Field label="Time zone"><select className="mt-1 h-9 w-full rounded-md border border-line bg-white px-2 text-sm" value={f.timezone} onChange={set("timezone")} data-testid="settings-timezone-select">
              {TZS.map(([z, l]) => <option key={z} value={z}>{l}</option>)}</select></Field>
          </div>}
        </div>
        <button className="btn-primary" onClick={save} data-testid="settings-save-btn">Save settings</button>
      </div>
    </div>
  );
}
